import { memo, type ReactElement } from 'react';
import { NO_VALUE, personHeadline, type StorePerson } from './storePeople';

/**
 * The Stores table's "Deployed Employee" and "Reporting Manager" cells.
 *
 * One line per person in each cell, in the same order, so a person's manager sits beside
 * them on the same line of the next column. Every line is two rows tall — the name, and
 * the code under it in small grey — and a line with nothing to put under its name keeps
 * an empty second row, so the two cells' lines stay level however much each one knows.
 *
 * Memoised on the store's people list, which the page works out once per store: typing
 * in a form re-renders the page, not these ~5,000 cells.
 */

const CELL_CLASS = 'whitespace-nowrap px-6 py-4 text-left';

const Dash = (): ReactElement => <span className="text-gray-400">{NO_VALUE}</span>;

/** One person's line: what it leads with, and the code under it (none when it led with the code). */
function PersonLine({ name, code }: { name: string; code: string }): ReactElement {
  const headline = personHeadline(name, code);
  const under = name ? code : '';
  return (
    <li>
      <div className="text-gray-900">{headline || <Dash />}</div>
      {under ? (
        <div className="text-xs text-gray-500">{under}</div>
      ) : (
        // Holds the line's height so it stays level with its neighbour in the other column.
        <div className="text-xs" aria-hidden="true">{' '}</div>
      )}
    </li>
  );
}

interface PeopleCellProps {
  people: readonly StorePerson[];
}

export const DeployedEmployeeCell = memo(function DeployedEmployeeCell({ people }: PeopleCellProps): ReactElement {
  return (
    <td className={CELL_CLASS}>
      {people.length === 0 ? <Dash /> : (
        <ul className="space-y-2">
          {people.map((p, i) => <PersonLine key={`${p.code}-${i}`} name={p.name} code={p.code} />)}
        </ul>
      )}
    </td>
  );
});

export const ReportingManagerCell = memo(function ReportingManagerCell({ people }: PeopleCellProps): ReactElement {
  return (
    <td className={CELL_CLASS}>
      {people.length === 0 ? <Dash /> : (
        <ul className="space-y-2">
          {people.map((p, i) => <PersonLine key={`${p.code}-${i}`} name={p.managerName} code={p.managerCode} />)}
        </ul>
      )}
    </td>
  );
});
